// New API service for the chatbot
export interface ChatApiResponse {
  response: string;
  conversation_id: string;
  pdf_url?: string;
}

export interface ChatApiRequest {
  message: string;
  conversation_id?: string;
  city?: string;
}

class ChatApiService {
  private baseUrl = 'https://aichat-api.viewprogis.com';
  private conversationId: string | null = null;

  // Extract city from URL parameters or embed config
  private getCityFromUrl(): string | null {
    // Check current window first (for embed mode)
    let embedCity = (window as any).__VIEWPRO_CITY__;
    
    // If not found, check if there's an iframe and look in its window
    if (!embedCity) {
      const iframe = document.querySelector('iframe[srcdoc]') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        embedCity = (iframe.contentWindow as any).__VIEWPRO_CITY__;
      }
    }
    
    // If embed city found, format it for API
    if (embedCity) {
      // Format: aubrey-tx -> city-of-aubrey
      const cityName = embedCity.split('-')[0];
      return `city-of-${cityName}`;
    }
    
    // Fall back to URL parameters (for standalone mode)
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for city parameter formats like ?aubrey-tx or ?community=aubrey-tx
    const keys = Array.from(urlParams.keys());
    
    for (const key of keys) {
      const value = urlParams.get(key);
      
      // Format 1: ?aubrey-tx (key is the city, value is empty)
      if (key && !value && key.includes('-')) {
        // Extract city name (first part before hyphen): aubrey-tx -> aubrey
        const cityName = key.split('-')[0];
        return `city-of-${cityName}`;
      }
      // Format 2: ?community=aubrey-tx
      if (key === 'community' && value) {
        // Extract city name (first part before hyphen): aubrey-tx -> aubrey
        const cityName = value.split('-')[0];
        return `city-of-${cityName}`;
      }
    }
    
    return null;
  }

  async sendMessage(message: string): Promise<ChatApiResponse> {
    try {
      const payload: ChatApiRequest = {
        message: message.trim(),
      };

      // Add conversation_id if we have one from previous messages
      if (this.conversationId) {
        payload.conversation_id = this.conversationId;
      }

      // Add city parameter - use detected city or default to kings-mountain
      const city = this.getCityFromUrl();
      payload.city = city || 'kings-mountain';

      console.log('Sending message to new API:', payload);

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data: ChatApiResponse = await response.json();
      
      // Store conversation_id for future messages
      if (data.conversation_id) {
        this.conversationId = data.conversation_id;
      }

      console.log('Received response from new API:', data);
      return data;
    } catch (error) {
      console.error('Error calling chat API:', error);
      throw error;
    }
  }

  // Reset conversation (for new chats)
  resetConversation(): void {
    this.conversationId = null;
  }

  // Get current conversation ID
  getConversationId(): string | null {
    return this.conversationId;
  }

  // Set conversation ID (if needed)
  setConversationId(id: string): void {
    this.conversationId = id;
  }
}

// Export singleton instance
export const chatApiService = new ChatApiService();
